import { Badge, Container } from "reactstrap";
import { setSelectedScoutingTrait, upsertMapLayer } from "../actions/resultMap";
import { useClickAway, useKey } from "react-use";
import { useDispatch, useSelector } from "react-redux";
import { useRef, useState } from "react";

import { DEFAULT_MAP_LAYERS } from "../constants";
import { FilteringProfileAbstract } from "./FilteringProfileAbstract/FilteringProfileAbstract";
import { FilteringProfileBadge } from "../components/Badges/FilteringProfileBadge/FilteringProfileBadge";
import PropTypes from "prop-types";
import { ScoutingBadge } from "../components/Badges/ScoutingBadge/ScoutingBadge";
import { TraitBadge } from "../components/Badges/TraitBadge/TraitBadge";
import { TraitSelector } from "../analyticsDashboard/components/traitSelector/traitSelector";
import { TrialDateSelector } from "../components/TrialDateSelector/TrialDateSelector";
import { format } from "date-fns";
import { useKeyboardShortcut } from "../hooks/useKeyboardShortcut";

export const BottomBar = ({ toggleOffcanvas }) => {
  const [
    trial,
    filteringProfile,
    selectedTrait,
    selectedDate,
    scoutingDates,
    selectedScoutingTrait,
    scoutingFeatures,
    scoutingTraits,
  ] = useSelector(({ resultMap }) => [
    resultMap.trial,
    resultMap.filteringProfile,
    resultMap.selectedTrait,
    resultMap.trial_date,
    resultMap.scoutingDates,
    resultMap.selectedScoutingTrait,
    resultMap.scoutingFeatures,
    resultMap.scoutingTraits,
  ]);
  const dispatch = useDispatch();

  const [isTraitSelectorOpen, setIsTraitSelectorOpen] = useState(false);
  const traitSelectorRef = useRef(null);
  useClickAway(traitSelectorRef, () => setIsTraitSelectorOpen(false));

  const [isOrthoSelectorOpen, setIsOrthoSelectorOpen] = useState(false);
  const orthoSelectorRef = useRef(null);
  useClickAway(orthoSelectorRef, () => setIsOrthoSelectorOpen(false));

  const [isScoutingTraitSelectorOpen, setIsScoutingTraitSelectorOpen] =
    useState(false);

  const scoutingTraitSelectorRef = useRef(null);
  useClickAway(scoutingTraitSelectorRef, () => {
    setIsScoutingTraitSelectorOpen(false);
  });

  const [isFilteringProfileAbstractOpen, setIsFilteringProfileAbstractOpen] =
    useState(false);

  const orthoLayer = useSelector(({ resultMap }) =>
    resultMap.mapLayers.find(({ id }) => id === DEFAULT_MAP_LAYERS.ORTHO_LAYER)
  );

  const ortho = orthoLayer?.name;

  const setSelectedOrtho = (orthoName) => {
    dispatch(upsertMapLayer({ ...orthoLayer, name: orthoName }));
  };

  // Press escape to close every opened selector
  useKey("Escape", () => {
    setIsTraitSelectorOpen(false);
    setIsOrthoSelectorOpen(false);
  });

  useKeyboardShortcut("t", () => {
    setIsTraitSelectorOpen(true);
  });

  return (
    trial && (
      <Container fluid className="bottom-bar">
        <div className="bottom-bar-items">
          <div className="bottom-bar-item">
            <TrialDateSelector
              className="bottom-bar-anchor"
              popoverClassName="bottom-bar-popover"
            />
            <span className="bottom-bar-anchor">
              {isTraitSelectorOpen && (
                <div className="bottom-bar-popover" ref={traitSelectorRef}>
                  <TraitSelector />
                </div>
              )}
              {Object.keys(selectedTrait).length > 0 && (
                <TraitBadge
                  trait={selectedTrait}
                  onClick={() => setIsTraitSelectorOpen(true)}
                />
              )}
            </span>
            <span className="bottom-bar-anchor">
              {isOrthoSelectorOpen && (
                <div className="bottom-bar-popover" ref={orthoSelectorRef}>
                  <div className="powerdash-component gap-2 d-flex flex-column overflow-auto">
                    {Object.keys(trial.ortho_layer[selectedDate])
                      .sort((a, b) =>
                        a.toLowerCase().localeCompare(b.toLowerCase())
                      )
                      .map((orthoName) => (
                        <Badge
                          pill
                          key={orthoName}
                          onClick={() => setSelectedOrtho(orthoName)}
                          className={`hiphen-badge d-flex align-items-center gap-1 elevated clickable ${
                            ortho === orthoName ? " active" : ""
                          }`}
                          data-tooltip-id="tooltip"
                          data-tooltip-content={orthoName}
                          data-tooltip-place="right"
                        >
                          <i className="fa-solid fa-layer-group" />
                          <span className="text-ellipsis">{orthoName}</span>
                        </Badge>
                      ))}
                  </div>
                </div>
              )}
              {orthoLayer &&
                (ortho !== "plain" ||
                  Object.keys(trial.ortho_layer[selectedDate] ?? {}).length >
                    1) && (
                  <Badge
                    pill
                    className="hiphen-badge elevated active clickable gap-2"
                    onClick={() => setIsOrthoSelectorOpen(true)}
                  >
                    <i className="fa-solid fa-layer-group" />
                    <span className="text-ellipsis">{ortho}</span>
                  </Badge>
                )}
            </span>
            <span className="bottom-bar-anchor">
              {Object.keys(scoutingTraits).length > 0 &&
                isScoutingTraitSelectorOpen && (
                  <div
                    className="bottom-bar-scouting-popover"
                    ref={scoutingTraitSelectorRef}
                  >
                    <div className="powerdash-component flex-column gap-1 overflow-auto">
                      <div className="d-flex gap-1 justify-content-between">
                        <div className="d-flex gap-1 align-items-center">
                          <Badge color="danger" className="p-1">
                            <i className="fa-solid fa-camera me-1" />
                            {scoutingFeatures.length}
                          </Badge>
                          Scouting
                        </div>
                        <Badge color="danger">
                          {format(scoutingDates[0], "yyyy-MM-dd")} →{" "}
                          {format(scoutingDates[1], "yyyy-MM-dd")}
                        </Badge>
                      </div>
                      {Object.entries(scoutingTraits).map(([key, value]) => {
                        return (
                          <Badge
                            key={key}
                            className={`scouting-trait-badge elevated clickable ${
                              value.label === selectedScoutingTrait?.label
                                ? "active"
                                : ""
                            }`}
                            onClick={() => {
                              dispatch(
                                setSelectedScoutingTrait({
                                  ...value,
                                  key,
                                })
                              );
                            }}
                          >
                            {value.label}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}
              {scoutingDates[0] && scoutingDates[1] && (
                <ScoutingBadge onClick={setIsScoutingTraitSelectorOpen} />
              )}
            </span>
          </div>
          <div className="bottom-bar-item">
            {filteringProfile.id && isFilteringProfileAbstractOpen && (
              <div className="bottom-bar-popover bottom-bar-popover--end">
                <FilteringProfileAbstract filteringProfile={filteringProfile} />
              </div>
            )}
            <FilteringProfileBadge
              filteringProfile={filteringProfile}
              onClick={toggleOffcanvas}
              onMouseEnter={() => setIsFilteringProfileAbstractOpen(true)}
              onMouseLeave={() => setIsFilteringProfileAbstractOpen(false)}
            />
          </div>
        </div>
      </Container>
    )
  );
};

BottomBar.propTypes = {
  toggleOffcanvas: PropTypes.func.isRequired,
};
